// Finance-owned, versioned deal-underwriting policy contract (#5840). This module persists
// the recognition, CTA, tax, and scope conventions Finance applies when underwriting a
// governed M&A promise set. It does not calculate any financial result — see
// apps/api/src/decision-modules/finance/deal-underwriting for the read source, and #5552 for
// the formula runtime that consumes an admitted policy.
import { createHash } from 'crypto';

import type { CurrencyRef, DealRef, FiscalCalendarRef, PolicyRef, PromiseSetRef, TenantRef } from './ma-deal-refs';

declare const financeDealUnderwritingRefBrand: unique symbol;

type FinanceDealUnderwritingOpaqueRef<Name extends string> = string & {
  readonly [financeDealUnderwritingRefBrand]: Name;
};

export type GovernedPeriodRef = FinanceDealUnderwritingOpaqueRef<'governed_period'>;
export type AccountingBasisRef = FinanceDealUnderwritingOpaqueRef<'accounting_basis'>;
export type GovernedActorOrSeatRef = FinanceDealUnderwritingOpaqueRef<'governed_actor_or_seat'>;
export type GovernedDocumentRef = FinanceDealUnderwritingOpaqueRef<'governed_document'>;
export type GovernedAssumptionRef = FinanceDealUnderwritingOpaqueRef<'governed_assumption'>;
export type GovernedSourceRef = FinanceDealUnderwritingOpaqueRef<'governed_source'>;

/** A generic governed pointer used only by `{ kind: 'reference' }` assumption values. */
export type GovernedReference = Readonly<{ refKind: string; id: string }>;

/** Every gap code the Finance deal-underwriting policy source/reader can report, other than
 * `assumption_value_missing` (which carries its own structured fields below). Closed so a new
 * gap path must add its code here, not fall back to a bare, un-typed string. */
export type FinanceDealUnderwritingOtherGapCode =
  | 'missing_tenant'
  | 'policy_ref_not_found'
  | 'policy_ref_identity_mismatch'
  | 'no_admitted_policy'
  | 'admitted_policy_missing'
  | 'admitted_policy_identity_mismatch'
  // #5889 terra HOLD round 2 — a caller who named SOME of dealRef/promiseSetRef/horizonRef
  // must never have the rest silently filled in from the tenant-unique fallback (that would
  // resolve a policy for a deal the caller never asked about). Named apart from
  // `no_admitted_policy` / `*_identity_mismatch` so this "you told me part of the identity,
  // finish it or tell me none of it" case is never confused with "I looked and found nothing".
  | 'partial_deal_identity';

/**
 * A gap reported by the policy source or reader. `assumption_value_missing` — the
 * corrupted-row case the reader must never paper over with a fabricated `0` / `false` / `{}`
 * (terra HOLD @ 7f4ee311c5) — carries the offending `assumptionKey` typed to the closed
 * 13-key union and `policySetVersion` typed to (and REQUIRED as) the policy set's own `version`
 * type, not interpolated only into free-text `message` where callers would have to re-parse
 * prose to get a structured value back. `policySetVersion` is required, never optional: every
 * producer of this gap — including a direct `readAssumptions()` call with no policy row in
 * hand yet — must resolve the owning policy set's version before reporting the gap, rather
 * than reporting a gap that can silently omit which version was corrupted. `code` is a proper
 * discriminant (not a bare `string` on the other branch) so a caller can narrow on
 * `gap.code === 'assumption_value_missing'` and see the structured fields.
 */
export type NamedGap =
  | Readonly<{ code: FinanceDealUnderwritingOtherGapCode; message: string }>
  | Readonly<{
      code: 'assumption_value_missing';
      message: string;
      assumptionKey: ClosedFinanceDealUnderwritingAssumptionKey;
      policySetVersion: FinanceDealUnderwritingPolicySetV1['version'];
    }>;

export type PromiseBand = 'conservative' | 'base' | 'stretch';
export type FinanceUnderwritingGovernanceStatus = 'draft' | 'approved' | 'superseded';
export type FinanceUnderwritingEvidenceBasis = 'modeled' | 'observed' | 'unverified';

export const CLOSED_FINANCE_DEAL_UNDERWRITING_ASSUMPTION_KEYS = Object.freeze([
  'result_scope',
  'benefit_recognition_method',
  'benefit_year_one_recognition_factor',
  'cta_recognition_method',
  'cta_year_one_p_and_l_factor',
  'planning_tax_rate',
  'planning_tax_rate_basis',
  'tax_application_method',
  'negative_pre_tax_tax_benefit_policy',
  'standalone_performance_included',
  'dis_synergy_or_leakage_included',
  'financing_included',
  'purchase_accounting_included',
] as const);

export type ClosedFinanceDealUnderwritingAssumptionKey =
  (typeof CLOSED_FINANCE_DEAL_UNDERWRITING_ASSUMPTION_KEYS)[number];

/**
 * Closed enum-value vocabulary for `{ kind: 'enum' }` assumptions. Membership is closed at
 * the union level; per-key domain is enforced separately by
 * `FINANCE_UNDERWRITING_ENUM_VALUES_BY_KEY` so an enum value valid for one key cannot be
 * silently accepted for another.
 */
export const CLOSED_FINANCE_UNDERWRITING_ASSUMPTION_VALUES = Object.freeze([
  'promised_synergy_less_approved_cta_only',
  'full_annual_run_rate_in_year_one',
  'full_approved_envelope_in_year_one_p_and_l',
  'management_planning_rate_not_gaap_effective_tax_rate',
  'simple_rate_on_positive_pre_tax_contribution',
  'withhold_without_explicit_policy',
] as const);

export type ClosedFinanceUnderwritingAssumptionValue =
  (typeof CLOSED_FINANCE_UNDERWRITING_ASSUMPTION_VALUES)[number];

export const FINANCE_UNDERWRITING_ENUM_VALUES_BY_KEY: Readonly<
  Partial<Record<ClosedFinanceDealUnderwritingAssumptionKey, readonly ClosedFinanceUnderwritingAssumptionValue[]>>
> = Object.freeze({
  result_scope: ['promised_synergy_less_approved_cta_only'],
  benefit_recognition_method: ['full_annual_run_rate_in_year_one'],
  cta_recognition_method: ['full_approved_envelope_in_year_one_p_and_l'],
  planning_tax_rate_basis: ['management_planning_rate_not_gaap_effective_tax_rate'],
  tax_application_method: ['simple_rate_on_positive_pre_tax_contribution'],
  negative_pre_tax_tax_benefit_policy: ['withhold_without_explicit_policy'],
});

const FINANCE_UNDERWRITING_BOOLEAN_KEYS: readonly ClosedFinanceDealUnderwritingAssumptionKey[] = [
  'standalone_performance_included',
  'dis_synergy_or_leakage_included',
  'financing_included',
  'purchase_accounting_included',
];

const FINANCE_UNDERWRITING_NUMBER_KEYS: readonly ClosedFinanceDealUnderwritingAssumptionKey[] = [
  'benefit_year_one_recognition_factor',
  'cta_year_one_p_and_l_factor',
  'planning_tax_rate',
];

export type FinanceDealUnderwritingAssumptionValueV1 =
  | Readonly<{ kind: 'number'; value: number; unit: 'bps' | 'usd' }>
  | Readonly<{ kind: 'boolean'; value: boolean }>
  | Readonly<{ kind: 'enum'; value: ClosedFinanceUnderwritingAssumptionValue }>
  | Readonly<{ kind: 'reference'; value: GovernedReference }>;

export type FinanceDealUnderwritingPolicySetV1 = Readonly<{
  schemaVersion: 'finance_deal_underwriting_policy_set_v1';

  policySetRef: PolicyRef;
  tenantRef: TenantRef;

  dealRef: DealRef;
  promiseSetRef: PromiseSetRef;

  purposeKind: 'management_underwriting';
  horizonRef: GovernedPeriodRef;

  subjectBand: PromiseBand;
  referenceBand: PromiseBand;
  comparisonBasis: 'scenario';

  reportingCurrencyRef: CurrencyRef;
  fiscalCalendarRef: FiscalCalendarRef;
  accountingBasisRef: AccountingBasisRef;

  version: number;
  governanceStatus: FinanceUnderwritingGovernanceStatus;
  evidenceBasis: FinanceUnderwritingEvidenceBasis;

  effectiveFrom: string;
  effectiveTo: string | null;
  supersedesPolicySetRef: PolicyRef | null;

  approvedAt: string | null;
  approvedByRef: GovernedActorOrSeatRef | null;
  sourceDocumentRefs: readonly GovernedDocumentRef[];

  contentDigest: string;
  assumptionRefs: readonly GovernedAssumptionRef[];
}>;

export type FinanceDealUnderwritingAssumptionV1 = Readonly<{
  schemaVersion: 'finance_deal_underwriting_assumption_v1';

  assumptionRef: GovernedAssumptionRef;
  policySetRef: PolicyRef;
  tenantRef: TenantRef;

  assumptionKey: ClosedFinanceDealUnderwritingAssumptionKey;
  value: FinanceDealUnderwritingAssumptionValueV1;

  horizonRef: GovernedPeriodRef | null;
  sourceClass: string;
  governanceStatus: FinanceUnderwritingGovernanceStatus;
  evidenceBasis: FinanceUnderwritingEvidenceBasis;

  sourceRefIds: readonly GovernedSourceRef[];
  approvedAt: string | null;
  approvedByRef: GovernedActorOrSeatRef | null;

  contentDigest: string;
}>;

export type FinanceDealUnderwritingAdmissionV1 = Readonly<{
  tenantRef: TenantRef;
  dealRef: DealRef;
  promiseSetRef: PromiseSetRef;
  purposeKind: 'management_underwriting';
  horizonRef: GovernedPeriodRef;
  activePolicySetRef: PolicyRef;
  contentDigest: string;
  approvedAt: string | null;
  approvedByRef: GovernedActorOrSeatRef | null;
}>;

export class FinanceDealUnderwritingPolicyValidationError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'FinanceDealUnderwritingPolicyValidationError';
  }
}

export class FinanceDealUnderwritingPolicyImmutableError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'FinanceDealUnderwritingPolicyImmutableError';
  }
}

export class FinanceDealUnderwritingPolicyDigestMismatchError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'FinanceDealUnderwritingPolicyDigestMismatchError';
  }
}

/** Sha256 content digest over the policy set plus its assumption rows, canonicalized. */
export function computeFinanceDealUnderwritingContentDigest(
  policySet: Omit<FinanceDealUnderwritingPolicySetV1, 'contentDigest'>,
  assumptions: readonly Omit<FinanceDealUnderwritingAssumptionV1, 'contentDigest'>[],
): string {
  const canonical = canonicalJson({ policySet, assumptions: [...assumptions].sort(byAssumptionKey) });
  return `sha256:${createHash('sha256').update(canonical).digest('hex')}`;
}

function byAssumptionKey(
  left: Omit<FinanceDealUnderwritingAssumptionV1, 'contentDigest'>,
  right: Omit<FinanceDealUnderwritingAssumptionV1, 'contentDigest'>,
): number {
  return left.assumptionKey.localeCompare(right.assumptionKey);
}

/**
 * Validates one assumption's key/value/unit combination against the closed vocabulary. Throws
 * `FinanceDealUnderwritingPolicyValidationError` on any mismatch — storage law #6/#7: rates
 * are fixed-scale basis points, and no default value is ever substituted.
 */
export function assertValidFinanceDealUnderwritingAssumptionValue(
  assumptionKey: ClosedFinanceDealUnderwritingAssumptionKey,
  value: FinanceDealUnderwritingAssumptionValueV1,
): void {
  if (!CLOSED_FINANCE_DEAL_UNDERWRITING_ASSUMPTION_KEYS.includes(assumptionKey)) {
    throw new FinanceDealUnderwritingPolicyValidationError(
      `Unknown Finance deal-underwriting assumption key: ${String(assumptionKey)}`,
    );
  }
  if (FINANCE_UNDERWRITING_BOOLEAN_KEYS.includes(assumptionKey)) {
    assertBooleanAssumption(assumptionKey, value);
    return;
  }
  if (FINANCE_UNDERWRITING_NUMBER_KEYS.includes(assumptionKey)) {
    assertNumberAssumption(assumptionKey, value);
    return;
  }
  assertEnumAssumption(assumptionKey, value);
}

function assertBooleanAssumption(
  assumptionKey: ClosedFinanceDealUnderwritingAssumptionKey,
  value: FinanceDealUnderwritingAssumptionValueV1,
): void {
  if (value.kind !== 'boolean') {
    throw new FinanceDealUnderwritingPolicyValidationError(
      `Assumption key "${assumptionKey}" requires a boolean value.`,
    );
  }
}

function assertNumberAssumption(
  assumptionKey: ClosedFinanceDealUnderwritingAssumptionKey,
  value: FinanceDealUnderwritingAssumptionValueV1,
): void {
  if (value.kind !== 'number') {
    throw new FinanceDealUnderwritingPolicyValidationError(
      `Assumption key "${assumptionKey}" requires a number value.`,
    );
  }
  if (value.unit !== 'bps') {
    throw new FinanceDealUnderwritingPolicyValidationError(
      `Assumption key "${assumptionKey}" must be stored in basis points, never a bare percentage or float ratio.`,
    );
  }
  if (!Number.isInteger(value.value)) {
    throw new FinanceDealUnderwritingPolicyValidationError(
      `Assumption key "${assumptionKey}" must be an exact integer basis-point value.`,
    );
  }
}

function assertEnumAssumption(
  assumptionKey: ClosedFinanceDealUnderwritingAssumptionKey,
  value: FinanceDealUnderwritingAssumptionValueV1,
): void {
  if (value.kind !== 'enum') {
    throw new FinanceDealUnderwritingPolicyValidationError(
      `Assumption key "${assumptionKey}" requires an enum value.`,
    );
  }
  const allowed = FINANCE_UNDERWRITING_ENUM_VALUES_BY_KEY[assumptionKey] ?? [];
  if (!allowed.includes(value.value)) {
    throw new FinanceDealUnderwritingPolicyValidationError(
      `"${value.value}" is not a governed enum value for assumption key "${assumptionKey}".`,
    );
  }
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value === 'boolean' || typeof value === 'number') {
    return stringifyPrimitive(value);
  }
  if (typeof value === 'string') return stringifyPrimitive(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (isPlainRecord(value)) return canonicalRecord(value);
  throw new TypeError('Finance deal-underwriting policy content must be JSON-compatible.');
}

function canonicalRecord(value: Readonly<Record<string, unknown>>): string {
  return `{${Object.entries(value)
    .filter(([key]) => key !== 'contentDigest')
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, entry]) => `${stringifyPrimitive(key)}:${canonicalJson(entry)}`)
    .join(',')}}`;
}

function isPlainRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringifyPrimitive(value: string | number | boolean | null): string {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    throw new TypeError('Finance deal-underwriting policy content must be JSON-compatible.');
  }
  return serialized;
}

/**
 * Selection precedence for reading a policy set. Precedence order per #5840: an explicit
 * frozen reference wins outright; failing that, a unique admitted policy for the identity;
 * anything else is missing or ambiguous. A caller with a prior-turn frozen `policySetRef`
 * should pass it as `explicitPolicySetRef` — this module has no notion of "prior turn" itself.
 */
export type FinanceDealUnderwritingPolicySelectionInput = Readonly<{
  explicitPolicySetRef?: PolicyRef;
  admittedPolicySetRefs: readonly PolicyRef[];
}>;

export type FinanceDealUnderwritingPolicySelectionResult =
  | Readonly<{ kind: 'selected'; policySetRef: PolicyRef }>
  | Readonly<{ kind: 'missing' }>
  | Readonly<{ kind: 'ambiguous'; policySetRefs: readonly PolicyRef[] }>;

export function selectFinanceDealUnderwritingPolicySetRef(
  input: FinanceDealUnderwritingPolicySelectionInput,
): FinanceDealUnderwritingPolicySelectionResult {
  if (input.explicitPolicySetRef) {
    return { kind: 'selected', policySetRef: input.explicitPolicySetRef };
  }
  if (input.admittedPolicySetRefs.length === 1) {
    return { kind: 'selected', policySetRef: input.admittedPolicySetRefs[0] };
  }
  if (input.admittedPolicySetRefs.length === 0) return { kind: 'missing' };
  return { kind: 'ambiguous', policySetRefs: input.admittedPolicySetRefs };
}
